<script setup>
import {usePortalContentAndDesignStore} from "../../../../stores/partners/portalContentAndDesignStore";
import {defineProps, watch} from "vue";

const props = defineProps({
    authView: {
        type: Boolean,
        default: false
    }
});

const portalContentAndDesignStore =  usePortalContentAndDesignStore()

const handlePortalLayoutBrandPrimaryColorChange = (event) => {
    portalContentAndDesignStore.portalLayoutSettings.portal_layout_brand_primary_color = event.finalValue
    if (portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_primary_color_as_partner_and_pages_primary) {
        portalContentAndDesignStore.portalLayoutSettings.portal_layout_button_color = event.finalValue;
        portalContentAndDesignStore.portalLayoutSettings.portal_layout_link_color = event.finalValue;
        portalContentAndDesignStore.portalLayoutSettings.public_page_link_color = event.finalValue;
        portalContentAndDesignStore.portalLayoutSettings.public_page_content_link_color = event.finalValue;
        portalContentAndDesignStore.portalLayoutSettings.public_page_content_button_color = event.finalValue;
    }
};

watch(() => portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family, (newFont) => {
    const fontName = newFont || 'Inter';
    const formattedForCSS = fontName.replace(/\+/g, ' ');
    const formattedForGoogle = fontName.replace(/ /g, '+');

    // Set font-family on <html>
    document.documentElement.style.fontFamily = `"${formattedForCSS}", sans-serif`;

    // Inject or update the Google Fonts <link>
    const existingLink = document.getElementById('google-fonts-link');
    const href = `https://fonts.googleapis.com/css2?family=${formattedForGoogle}:wght@400;500;600;700&display=swap`;

    if (existingLink) {
        existingLink.setAttribute('href', href);
    } else {
        const link = document.createElement('link');
        link.id = 'google-fonts-link';
        link.rel = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
    }
}, { immediate: true });

</script>

<template>
    <file-upload
        v-model:src="portalContentAndDesignStore.srcRoutes.portal_layout_file_logo"
        no_src="/images/upload-image.png"
        accept="image/*"
        title="Logo"
        name="portal_layout_file_logo"
        :upload_url="portalContentAndDesignStore.getFileUploadRoute"
        :delete_url="portalContentAndDesignStore.getFileDeleteRoute"
    ></file-upload>

    <file-upload
        v-model:src="portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol"
        no_src="/images/upload-image.png"
        accept="image/*"
        title="Symbol"
        name="portal_layout_file_symbol"
        :upload_url="portalContentAndDesignStore.getFileUploadRoute"
        :delete_url="portalContentAndDesignStore.getFileDeleteRoute"
    ></file-upload>

    <file-upload
        v-model:src="portalContentAndDesignStore.srcRoutes.portal_layout_file_favicon"
        no_src="/images/upload-image.png"
        accept="image/*"
        title="Favicon"
        name="portal_layout_file_favicon"
        :upload_url="portalContentAndDesignStore.getFileUploadRoute"
        :delete_url="portalContentAndDesignStore.getFileDeleteRoute"
    ></file-upload>

    <div></div>

    <select-field
        label="Font family"
        id="portal_layout_brand_font_family"
        name="portal_layout_brand_font_family"
        :model-value="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family || 'Inter'"
        @update:model-value="portalContentAndDesignStore.portalLayoutSettings.portal_layout_brand_font_family = $event"
    >
        <option value="Inter"
                :selected="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family === 'Inter'">
            Inter
        </option>
        <option value="Roboto"
                :selected="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family === 'Roboto'">
            Roboto
        </option>
        <option value="Open+Sans"
                :selected="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family === 'Open+Sans'">
            Open Sans
        </option>
        <option value="Lato"
                :selected="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family === 'Lato'">
            Lato
        </option>
        <option value="Ubuntu"
                :selected="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family === 'Ubuntu'">
            Ubuntu
        </option>
        <option value="Raleway"
                :selected="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family === 'Raleway'">
            Raleway
        </option>
        <option value="Montserrat"
                :selected="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family === 'Montserrat'">
            Montserrat
        </option>
        <option value="Poppins"
                :selected="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family === 'Poppins'">
            Poppins
        </option>
        <option value="Nunito"
                :selected="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family === 'Nunito'">
            Nunito
        </option>
        <option value="Titillium+Web"
                :selected="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family === 'Titillium+Web'">
            Titillium Web
        </option>
    </select-field>

    <div>
        <radio-group-inline-color
            label="Brand color"
            name="portal_layout_brand_primary_color"
            :model-value="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_primary_color || 'teal-600'"
            @picked="handlePortalLayoutBrandPrimaryColorChange($event)"
            :options="[
                { value: 'indigo-700', label: 'bg-indigo-700', label_is_class: true},
                { value: 'blue-700', label: 'bg-blue-700', label_is_class: true},
                { value: 'cyan-700', label: 'bg-cyan-700', label_is_class: true},
                { value: 'teal-600', label: 'bg-teal-600', label_is_class: true},
                { value: 'black', label: 'bg-black', label_is_class: true},
                { value: 'custom', label: 'Custom', custom: { name: 'portal_layout_brand_primary_color', placeholder: '', type: 'input' }},
            ]"
        >
        </radio-group-inline-color>
        <toggle
            class="text-gray-500 mt-3.5"
            name="portal_layout_brand_primary_color_as_partner_and_pages_primary"
            :model-value="!!portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_primary_color_as_partner_and_pages_primary"
            @update:model-value="portalContentAndDesignStore.portalLayoutSettings.portal_layout_brand_primary_color_as_partner_and_pages_primary = $event"
            title="Use as primary color"
        >
        </toggle>
    </div>
</template>

